import { ChangeEvent, FC, RefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  ClickAwayListener,
  MenuList,
  MenuItem as MUIMenuItem,
  Paper,
  Grow,
  Popper,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

// import { MenuItem } from '../../types';
import { Stack } from "@mui/system";

import {
  changeActivePage,
  selectSession,
  selectTheme,
  setTheme,
  useLogoutMutation,
} from "../../redux";
import { BackdropLoading } from "../basics";
import { YesNoQuestionModal } from "../../modals";
import { TypeRadio } from "../form-inputs";

type DropdownMenuProfileProps = {
  anchorRef: RefObject<HTMLAnchorElement>;
  open: boolean;
  setOpen(open: boolean): void;
};

const keyPrefix = "components.menu.DropdownMenuProfile";

export const DropdownMenuProfile: FC<DropdownMenuProfileProps> = ({
  anchorRef,
  open,
  setOpen,
}): JSX.Element => {
  const dispatch = useDispatch();
  const prevOpen = useRef(open);
  const session = useSelector(selectSession);
  const { theme } = useSelector(selectTheme);

  const { t } = useTranslation("translation", { keyPrefix });
  const [logoutMutation, { isLoading }] = useLogoutMutation();

  const [showConfirmationLogoutModal, setShowConfirmationLogoutModal] =
    useState<boolean>(false);
  const [themeConfig, setThemeConfig] = useState<boolean>(false);

  const logout = async (): Promise<void> => {
    await logoutMutation(session.username!);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    )
      return;

    setOpen(false);
    setThemeConfig(false);
  };

  const items: TypeRadio[] = [
    {
      label: t("themeOptions.default"),
      value: "default",
    },
    {
      label: t("themeOptions.light"),
      value: "light",
    },
    {
      label: t("themeOptions.dark"),
      value: "dark",
    },
  ];

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  const goChangePassword = (): void => {
    setOpen(false);
    dispatch(changeActivePage({ activePage: "changePassword" }));
  };

  const goToUserInfo = (): void => {
    setOpen(false);
    dispatch(changeActivePage({ activePage: "userInfo" }));
  };

  const goReactions = (): void => {
    setOpen(false);
    dispatch(changeActivePage({ activePage: "reactions" }));
  };

  const goFavorites = (): void => {
    setOpen(false);
    dispatch(changeActivePage({ activePage: "favorites" }));
  };

  const handleChangeTheme = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as "default" | "light" | "dark";

    dispatch(setTheme(value));
  };

  const handleChangeShowTheme = () => {
    setThemeConfig(!themeConfig);
  };

  const showCloseAlert = () => {
    setOpen(false);
    setShowConfirmationLogoutModal(true);
  };

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }
    prevOpen.current = open;
    // eslint-disable-next-line
  }, [open]);

  return (
    <>
      {showConfirmationLogoutModal && (
        <YesNoQuestionModal
          message={t("modals.logout.message")}
          noLabel={t("modals.logout.no")}
          yesLabel={t("modals.logout.yes")}
          onClickYes={logout}
          onClickNo={() => {
            setShowConfirmationLogoutModal(false);
          }}
          onClose={() => {
            setShowConfirmationLogoutModal(false);
          }}
        />
      )}
      {isLoading && <BackdropLoading />}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        sx={{ zIndex: 1 }}
        placement="bottom-start"
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom-start" ? "left top" : "left bottom",
            }}
          >
            <Paper
              sx={{
                minWidth: 339,
                paddingTop: 0,
                paddingBottom: "6px",
                paddingRight: "6px",
                paddingLeft: "6px",
                marginTop: 1,
                borderRadius: "11px",
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem={open} onKeyDown={handleListKeyDown}>
                  {themeConfig ? (
                    <Stack>
                      <MUIMenuItem
                        onClick={() => {
                          setThemeConfig(false);
                        }}
                        sx={{ width: "100%" }}
                      >
                        <Stack direction="row" alignItems="center" width="100%">
                          <ArrowBackIcon
                            sx={{ fontSize: "1.3rem", marginRight: 2 }}
                          />
                          <Typography fontWeight="bold" fontSize={26}>
                            Tema
                          </Typography>
                        </Stack>
                      </MUIMenuItem>
                      <RadioGroup
                        value={theme}
                        onChange={handleChangeTheme}
                        sx={{
                          paddingX: 4,
                          gap: 1,
                        }}
                      >
                        {items.map(({ label, value }, index) => (
                          <FormControlLabel
                            value={value}
                            control={<Radio />}
                            label={label}
                          />
                        ))}
                      </RadioGroup>
                    </Stack>
                  ) : (
                    <Stack>
                      <MUIMenuItem
                        sx={{ width: "100%", paddingBottom: 0 }}
                        disableTouchRipple
                        autoFocus={false}
                      >
                        <ItemLabel
                          text={session.username!}
                          withArrow={false}
                          isTitle={true}
                        />
                      </MUIMenuItem>
                      {/* <MUIMenuItem
                    sx={{ width: '100%', paddingBottom: 0 }}
                    disableTouchRipple
                    autoFocus={false}
                  >
                    <ItemLabel
                      text={session.name!}
                      withArrow={false}
                      isData={true}
                    />
                  </MUIMenuItem> */}
                      {/* <MUIMenuItem
                    sx={{ width: '100%', paddingBottom: 0, paddingTop: 0 }}
                    disableTouchRipple
                    autoFocus={false}
                  >
                    <ItemLabel
                      text={session.firstLastName!}
                      withArrow={false}
                      isData={true}
                    />
                  </MUIMenuItem> */}
                      <MUIMenuItem
                        sx={{
                          width: "100%",
                          // paddingBottom: 0
                        }}
                        disableTouchRipple
                        autoFocus={false}
                      >
                        <ItemLabel
                          text={session.email!}
                          withArrow={false}
                          isData={true}
                        />
                      </MUIMenuItem>
                      {/* <MUIMenuItem
                    sx={{ width: '100%', paddingBottom: 0 }}
                    disableTouchRipple
                    autoFocus={false}
                  >
                    <ItemLabel
                      text={session.channel!}
                      withArrow={false}
                      isData={true}
                    />
                  </MUIMenuItem> */}
                      <MUIMenuItem
                        sx={{ width: "100%" }}
                        onClick={goToUserInfo}
                      >
                        <ItemLabel text={t("userInfo")} />
                      </MUIMenuItem>
                      <MUIMenuItem
                        sx={{ width: "100%" }}
                        onClick={goChangePassword}
                      >
                        <ItemLabel text={t("changePassword")} />
                      </MUIMenuItem>
                      <MUIMenuItem sx={{ width: "100%" }} onClick={goReactions}>
                        <ItemLabel text={t("reactions")} />
                      </MUIMenuItem>
                      <MUIMenuItem sx={{ width: "100%" }} onClick={goFavorites}>
                        <ItemLabel text={t("favorites")} />
                      </MUIMenuItem>
                      <MUIMenuItem
                        sx={{ width: "100%" }}
                        onClick={handleChangeShowTheme}
                      >
                        <ItemLabel text={t("theme")} />
                      </MUIMenuItem>
                      <MUIMenuItem
                        sx={{ width: "100%" }}
                        onClick={showCloseAlert}
                      >
                        <ItemLabel text={t("logout")} />
                      </MUIMenuItem>
                    </Stack>
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export const ItemLabel: FC<{
  text: string;
  withArrow?: boolean;
  isTitle?: boolean;
  isData?: boolean;
}> = ({ text, withArrow = true, isTitle = false, isData = false }) => {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      width="100%"
      paddingLeft={0}
      paddingRight={-1}
    >
      <>
        {isTitle ? (
          <Typography variant="h6" fontWeight="600">
            {text}
          </Typography>
        ) : isData ? (
          <Typography variant="subtitle1" fontWeight="500">
            {text}
          </Typography>
        ) : (
          <> {text}</>
        )}
        {withArrow && <ArrowForwardIosIcon sx={{ fontSize: "1rem" }} />}
      </>
    </Stack>
  );
};
